// src/pages/AuditArchivePage.tsx
//
// ★★★ WAVE 9 — THE AUDIT ARCHIVE, P2C1 §12.134 (rotation) and §12.135 (storage
// threshold assessment). Four routes that no screen reached until this wave.
//
// ══ WHAT THIS SUPERSEDES, AND THE ARGUMENT IT HAS TO ANSWER ══
//
// `docs/requirements/RTM.md`'s Rules section carried a `⚠ NO SCREEN:` declaration
// for each of the four, and the one for `POST /api/audit/archive/run` was not a
// gap — it was an ARGUMENT AGAINST building this control:
//
//   "There is no scheduler anywhere in this product (ADR 0009 Decision 4), so this
//    is an OPERATOR action with nothing behind it to schedule, and a button that
//    seals an archive segment on a click would imply an operational rhythm the
//    deployment does not have. … A screen becomes the right shape the day a
//    scheduler exists — not before."
//
// Every clause of that is still true. What changed is not the facts but the
// precedent: Wave 8 Task 8 met the identical objection on the outcome-file leg —
// *"pressing it hands nothing over"* — and resolved it the way this screen does,
// rather than by leaving the leg unclickable. **The control exists AND the
// deployment's own boundary is stated on screen in every state, from the server's
// own words where the server has them.** A leg nobody can click does not
// demonstrate, and `docs/delivery/DEMO-READINESS-PLAN.md` counts an unreachable
// route as a surface the customer does not have.
//
// ⚠ So the notice is not decoration and must not be softened: **no scheduler runs
// this.** Rotation happens when an operator presses the button on this screen or
// posts the request by hand, and never otherwise. A reader who takes the existence
// of a button for a nightly job has been misled by this screen, which is the one
// failure mode it is written to avoid.
//
// ══ ⚠ §12.135 ASKS FOR ALERTING AND THIS IS A POLL ══
//
// The clause asks for alerting as limits approach. `GET /api/audit/archive/status`
// is a POLLED assessment and this screen polls it on load and on demand — there is
// no push, no e-mail, no pager. The RTM already records that narrowing; it is
// repeated here because *a status nobody polls is not an alert*, and a screen that
// showed a green level without saying who is watching would read as one.
//
// ══ WHAT IS DELIBERATELY NOT ON THIS SCREEN ══
//
// **Ciphertext and the initialisation vector.** The route omits both from the
// segment summary by design, so the client type cannot carry them and this screen
// cannot render them by accident. What it shows is the metadata an operator needs
// to reconcile a segment — its event range, its counts, its key ids and its digest
// and signature — and nothing that would let a reader reconstruct the archived
// events outside the verification path.
import { plainProse } from '../lib/plain-words'
import { IconButton } from '../components/IconButton'
import { useEffect, useState, type ReactElement } from 'react'
import { SgdsAlert, SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { formatInstant } from '../lib/format'
import type { ArchiveAlertSweepResponse, ArchiveRunResponse, ArchiveVerification, EstateNotificationOutcome } from '../lib/types'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { StateBadge } from '../components/StatusBadge'

/**
 * ★★★ ADDED 2026-08-24 BY THE SORTABLE-HEADER SWEEP — eight plain headers on a
 * compliance surface, and the one an operator reconciles a sealed segment against
 * during an incident review.
 *
 * ⚠ Sorted on the SERVER, and here that is not a preference: the route CAPS this
 * list at 100 segments with no cursor and no offset, so the order decides which
 * segments are visible at all. See `ARCHIVE_SEGMENT_SORT_COLUMNS`.
 */
type SegmentSortKey =
  | 'id'
  | 'eventCount'
  | 'fromEventId'
  | 'fromOccurredAt'
  | 'algorithm'
  | 'contentDigest'
  | 'createdAt'

const SEGMENT_COLUMN_LABELS: Readonly<Record<SegmentSortKey, string>> = {
  id: 'Segment',
  eventCount: 'Events',
  fromEventId: 'Event ids',
  fromOccurredAt: 'Covering',
  algorithm: 'Keys',
  contentDigest: 'Digest',
  createdAt: 'Sealed',
}


/**
 * ⚠ **THE STANDING BOUNDARY, rendered in every state of this screen.**
 *
 * Not a one-time banner and not conditional on anything: the sentence has to be
 * true whether the backlog is empty, whether a rotation just sealed a segment, and
 * whether the last one refused. `SettlementBatchesPage` carries its own version of
 * this for the same reason — the moment a notice becomes conditional, the state
 * where it matters most is the state that hides it.
 */
function SchedulerBoundary() {
  return (
    <div className="pt-unbuilt">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)]">
        No scheduler runs any of this
      </p>
      <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
        Rotation happens when an operator presses <strong>Rotate now</strong> on this screen, or posts the request by
        hand, and at no other time. There is no nightly job, no cron entry and no queue behind it, deliberately:
        nothing in this deployment runs on its own. The storage assessment below is <strong>polled</strong> when this
        screen loads or when you press
        Refresh. PSD asks for an alert as limits approach: a breach can be <em>raised</em> as a
        notifiable event, by <strong>Check now and raise an alert</strong> below, or by
        <code className="pt-mono text-xs"> scripts/run-storage-alert-sweep.mjs</code>, but nothing in this
        deployment runs that on a timer either, and <strong>no notification rule names either audit event in any
        environment this repository ships</strong>, so raising one records it and sends it to nobody. A level of{' '}
        <strong>OK</strong> here means nobody was watching when you last looked.
      </p>
      <p className="mt-2 font-semibold">
        Treat this as an operator console, not as evidence that the archive is being maintained on a schedule.
      </p>
    </div>
  )
}

/**
 * ★★★ WAVE 13 (P9) — WHAT WAS RAISED, AND THE SENTENCE THAT STOPS IT READING AS
 * "SENT".
 *
 * P2C1 §12.135's alerting limb now raises a real notifiable event. The distance
 * between that and a person receiving a mail is three things this screen must
 * not let a reader collapse: a `app.notification_rule` naming the event type
 * (**none is seeded in any environment this repository ships** — who the
 * relevant parties are is stated nowhere in the pack, A-51/Q-37), a configured
 * mail transport, and the outbox actually running afterwards.
 *
 * So the affirmative case says **Recorded**, never "Sent", and states the
 * remaining conditions on the same line. A screen that rendered a green "alert
 * sent" from a `raised: true` would be making exactly the claim
 * `050_notification.sql`'s honesty column exists to refuse.
 */
function NotificationOutcome({ outcome }: { outcome: EstateNotificationOutcome }): ReactElement {
  if (outcome.raised) {
    return (
      <p className="text-sm mt-2">
        <strong>Alert event recorded.</strong> It is recorded in the notification log.
        Whether anybody receives it depends on a notification rule naming this event type (none is configured
        in this deployment) and on the outbox running afterwards. <strong>Recorded is not sent.</strong>
      </p>
    )
  }
  if (outcome.reason === 'LEVEL_OK') {
    return (
      <p className="text-sm mt-2">
        No alert was raised: the estate is within both thresholds, so there is nothing to alert on. An{' '}
        <strong>OK</strong> level still means nobody was watching when you pressed this.
      </p>
    )
  }
  /*
    ⚠ A FAILED RAISE IS RENDERED AS A FAILURE, in front of the person who pressed
    the button. The route already wrote RAISE_AUDIT_NOTIFICATION_FAILED to the
    trail, and swallowing it here would leave an operator believing the alert went
    out — the whole point of returning it in the body rather than only auditing it.
  */
  return (
    <SgdsAlert show variant="warning" title="The alert event could NOT be raised, and nobody has been told">
      {/*
        ⚠ THE HEADLINE SENTENCE IS SLOTTED CONTENT, not only the alert's `title`.
        `sgds-alert` renders its title inside its own shadow root, so the sentence
        that matters most would be absent from `textContent` — invisible to a DOM
        test, and absent from the light DOM a text-extraction tool or a copied page
        would carry. The title is kept for the component's own affordance; the
        sentence is here so it exists whether or not the element ever upgrades.
      */}
      <p className="text-sm font-semibold">The alert event could NOT be raised, and nobody has been told.</p>
      <p className="text-sm mt-1">{plainProse(outcome.reason)}</p>
      <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">
        The operation itself succeeded and is recorded; only the notification failed. It is in the trail as{' '}
        a failed notification, with its dedupe key. Trying again is
        safe: the dedupe key makes a repeat raise of the same event a no-op.
      </p>
    </SgdsAlert>
  )
}

/** A long hex digest, shortened for the table with the whole value on the element. */
function digest(value: string): ReactElement {
  return (
    <code className="pt-mono text-xs" title={value}>
      {value.length <= 20 ? value : `${value.slice(0, 12)}…${value.slice(-6)}`}
    </code>
  )
}

/**
 * ⚠ Bytes rendered at the scale a human reads, and the RAW figure kept on the
 * element. An operator comparing this against a disk allocation needs the exact
 * number; an operator scanning the page needs "1.2 GB".
 */
function bytes(value: number): ReactElement {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let scaled = value
  let unit = 0
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024
    unit += 1
  }
  return (
    <span className="pt-tabular" title={`${value.toLocaleString('en-SG')} bytes`}>
      {unit === 0 ? value.toLocaleString('en-SG') : scaled.toFixed(1)} {units[unit]}
    </span>
  )
}

/**
 * ★★ ALL FOUR VERDICTS, never folded into one.
 *
 * The repository's own `verifyArchiveSegment` header states the reason and this
 * screen holds to it: *"a segment can fail signature verification and still decrypt
 * (proving the SIGNATURE caught something GCM's own tag did not, or vice versa) —
 * collapsing them would hide exactly the distinction a real incident review
 * needs."* A single green tick would be the collapse.
 *
 * ⚠ `coveredEventsStillQueryable` is `undefined` when decryption failed, and that
 * is a THIRD state rather than a false: there is no covered-id list to check
 * against, so the query-after-archival proof did not run. Rendering it as "No"
 * would report a failed check that never happened.
 */
function VerificationVerdicts({ verification }: { verification: ArchiveVerification }) {
  const rows: readonly { label: string; value: boolean | undefined; note?: string }[] = [
    { label: 'Signature valid over the stored bytes', value: verification.signatureValid },
    { label: 'Decryptable', value: verification.decryptable },
    { label: 'Plaintext content digest matches', value: verification.contentDigestValid },
    {
      label: 'Every covered event still readable in audit.event',
      value: verification.coveredEventsStillQueryable,
      note: 'The query-after-archival proof, re-run on this call rather than trusted from when the segment was sealed.',
    },
  ]
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 max-w-2xl">
      {rows.map((row) => (
        <div key={row.label} className="col-span-2 grid grid-cols-subgrid items-start">
          <dt className="text-sm">
            {row.label}
            {row.note !== undefined && (
              <span className="block text-xs text-[var(--sgds-body-color-subtle)]">{plainProse(row.note)}</span>
            )}
          </dt>
          <dd className="text-sm font-semibold">
            {row.value === undefined ? (
              /*
                ⚠ NOT "No". The check did not run, and saying "No" would report a
                failure that was never tested — the same distinction between "not
                built" and "no data" this product has a standing rule about.
              */
              <span>Not checked: decryption failed, so there was no list to check</span>
            ) : row.value ? (
              <span>Yes</span>
            ) : (
              <span className="text-[var(--sgds-danger-color-default)]">NO</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function AuditArchivePage() {
  const segmentSort = useSort<SegmentSortKey>()
  /**
   * ★★★ THE PAGE OFFSET — 2026-08-24 (WAVE 18), and this screen is the reason it
   * mattered most.
   *
   * ⚠ Wave 17 moved this list's sort to the server and had the screen SAY the table was
   * a window onto a capped archive. On a screen whose whole purpose is proving that
   * NOTHING IS MISSING from the trail, "there may be more" is the wrong resting place —
   * and the demonstration estate holds exactly 100 segments, precisely the route's own
   * limit, so that notice fired on every single load of the demonstration.
   *
   * ⚠ Held here rather than inside `useOffsetPager` because the FETCH is keyed on it —
   * the same shape six sibling screens carry.
   */
  const [segmentOffset, setSegmentOffset] = useState(0)
  const segments = useApi(
    () => api.archiveSegments({ ...sortParams(segmentSort.sort), offset: segmentOffset }),
    [segmentSort.sort.column, segmentSort.sort.dir, segmentOffset],
  )
  const segmentPaging = segments.data?.paging
  // ⚠ THE PAGE SIZE COMES FROM THE SERVER, never a constant here. `1` while the first
  // request is in flight keeps the page arithmetic from dividing by zero.
  const segmentPager = useOffsetPager(segmentPaging?.limit ?? 1, segmentPaging?.total)
  useEffect(() => {
    if (segmentPager.offset !== segmentOffset) setSegmentOffset(segmentPager.offset)
  }, [segmentPager.offset, segmentOffset])
  /*
    ⚠ A SORT CHANGE RESTARTS THE WALK, both halves in one handler. Page three of one
    order is not page three of another, and resetting only the pager leaves this
    component's fetch offset stale for exactly one render.
  */
  const onSegmentSort = (column: SegmentSortKey) => {
    segmentSort.onSort(column)
    segmentPager.reset()
    setSegmentOffset(0)
  }
  const status = useApi(() => api.archiveStatus(), [])

  /**
   * ⚠ Held per SEGMENT, not as one "the verification" — an operator verifying three
   * segments in a row must be able to see all three answers. One shared slot would
   * silently replace the previous verdict and make the screen look like it had
   * re-verified something it had not.
   */
  const [verifications, setVerifications] = useState<Record<string, ArchiveVerification | string>>({})
  const [verifying, setVerifying] = useState<string | null>(null)
  const [runOutcome, setRunOutcome] = useState<ArchiveRunResponse | string | null>(null)
  const [running, setRunning] = useState(false)
  /**
   * ⚠ The sweep's own outcome, held apart from the rotation's. They are two
   * different operations answering two different limbs of §12.135, and one
   * shared slot would let a rotation's result silently replace a sweep's — the
   * same argument the per-segment `verifications` map already makes one field up.
   */
  const [sweepOutcome, setSweepOutcome] = useState<ArchiveAlertSweepResponse | string | null>(null)
  const [sweeping, setSweeping] = useState(false)

  /**
   * ★★ WAVE 26 — each write button FREEZES a sentence and asks; the write happens
   * only from the confirmation (`ConfirmWrite`'s header says why every
   * irreversible write does this now). One slot with a discriminator rather than
   * two booleans: the card renders beside the button that was pressed, and only
   * one question is on screen at a time — a second pending question on a
   * compliance surface would leave a reader unsure which "Yes" answers which.
   * `Verify` is read-only and deliberately does NOT ask (a prompt on a harmless
   * action teaches people to dismiss the prompt on a harmful one).
   */
  const [confirming, setConfirming] = useState<{ readonly kind: 'rotate' | 'sweep'; readonly summary: string } | null>(
    null,
  )

  const verify = async (segmentId: string): Promise<void> => {
    setVerifying(segmentId)
    try {
      const answer = await api.archiveVerify(segmentId)
      setVerifications((previous) => ({ ...previous, [segmentId]: answer.verification }))
    } catch (error: unknown) {
      // ⚠ The server's own message, verbatim. A 404 for a segment id that does not
      // exist and a 403 for a caller who may not read the archive are different
      // sentences and this screen must not merge them into "verification failed".
      setVerifications((previous) => ({
        ...previous,
        [segmentId]:
          error instanceof ApiError || error instanceof Error
            ? error.message
            : 'The verification could not be completed and the server gave no reason.',
      }))
    } finally {
      setVerifying(null)
    }
  }

  const askToRotate = (): void => {
    const backlog = status.data?.status.backlogCount
    setConfirming({
      kind: 'rotate',
      summary:
        `Seal the current unarchived backlog${backlog === undefined ? '' : ` (${backlog.toLocaleString('en-SG')} event(s) at the last reading)`} ` +
        'into a new signed, encrypted archive segment. Once written the segment cannot be unsealed, altered or ' +
        'removed; every event it covers stays readable in audit.event. There is no undo. A mistake is corrected by ' +
        'another record.',
    })
  }

  const rotate = async (): Promise<void> => {
    setConfirming(null)
    setRunning(true)
    try {
      const answer = await api.runArchiveRotation()
      setRunOutcome(answer)
      // ⚠ BOTH reads are refreshed, and only after the write returns. A sealed
      // segment changes the list AND the backlog the assessment reports, and a
      // screen that refreshed one would show a new segment beside a stale backlog —
      // two numbers that cannot both be true, on a compliance surface.
      segments.reload()
      status.reload()
    } catch (error: unknown) {
      setRunOutcome(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : 'The rotation could not be completed and the server gave no reason.',
      )
    } finally {
      setRunning(false)
    }
  }

  const askToSweep = (): void => {
    setConfirming({
      kind: 'sweep',
      summary:
        'Take a fresh storage reading and, if it is at or over a threshold, write a notifiable alert event to ' +
        'the notification log and queue it for sending. Recorded is not sent: whether anybody receives it ' +
        'depends on a notification rule naming the event. There is no undo. A mistake is corrected by another record.',
    })
  }

  const sweepAlerts = async (): Promise<void> => {
    setConfirming(null)
    setSweeping(true)
    try {
      const answer = await api.sweepArchiveAlerts()
      setSweepOutcome(answer)
      // ⚠ The assessment panel is refreshed from the SWEEP's own reading rather
      // than left stale beside it. The sweep took a fresh reading to decide; a
      // panel still showing the previous one would put two different byte counts
      // on the same screen, and a reader would have no way to tell which is now.
      status.reload()
    } catch (error: unknown) {
      setSweepOutcome(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : 'The sweep could not be completed and the server gave no reason.',
      )
    } finally {
      setSweeping(false)
    }
  }

  const rows = segments.data?.segments ?? []
  const assessment = status.data?.status

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Audit archive</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          Rotation of the audit trail into signed, encrypted segments, and the storage-threshold assessment. Sealing a segment removes nothing: every event a segment covers stays an
          ordinary, readable row in the trail, and <strong>verification proves that on every call</strong> rather than
          trusting it from when the segment was created.
        </p>
      </div>

      <SchedulerBoundary />

      {/* ── §12.135, the storage assessment ─────────────────────────────────── */}
      <div className="pt-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold mb-1">Storage assessment</h2>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              Polled, not pushed. The thresholds a fresh install uses are <strong>placeholders</strong>, not a measured
              production ceiling: the clause names no number and the pack furnishes none, so a real deployment
              sets <code className="pt-mono text-xs">PRS2_AUDIT_STORAGE_ALERT_*</code> from its own disk allocation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <IconButton glyph="refresh" label="Refresh" onClick={status.reload} />
            {/*
              ★★★ WAVE 13 (P9) — the PUSH half of §12.135, as a control beside the
              poll it shares a reading with.

              ⚠ Deliberately NOT folded into Refresh. Refresh is a read an operator
              may press a dozen times while watching a rotation; this one WRITES a
              notifiable event, and a read that quietly raised alerts would make the
              alert depend on somebody happening to look — the exact failure the
              event exists to fix.
            */}
            <SgdsButton
              variant="primary"
              type="button"
              loading={sweeping}
              disabled={sweeping || confirming !== null}
              onClick={askToSweep}
            >
              Check now and raise an alert
            </SgdsButton>
          </div>
        </div>

        {confirming?.kind === 'sweep' && (
          <div className="mt-4">
            <ConfirmWrite
              summary={confirming.summary}
              confirmLabel="Yes, raise alerts"
              onConfirm={() => void sweepAlerts()}
              onCancel={() => setConfirming(null)}
            />
          </div>
        )}

        {status.error !== null ? (
          <div className="mt-4">
            <ErrorCard title="Could not read the storage assessment" message={status.error} onRetry={status.reload} />
          </div>
        ) : status.initialLoading ? (
          <div className="mt-4">
            <LoadingCard label="Reading the storage assessment" rows={2} />
          </div>
        ) : assessment === undefined ? null : (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
            <dt className="text-sm font-semibold">Level</dt>
            <dd className="font-semibold">
              <StateBadge value={assessment.level} />
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">What the server says</dt>
            {/* ⚠ VERBATIM. The assessment's own sentence, never paraphrased on a compliance surface. */}
            <dd className="text-sm">{plainProse(assessment.message)}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">audit.event size</dt>
            <dd>{bytes(assessment.eventTableBytes)}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Archive table size</dt>
            <dd>{bytes(assessment.archiveTableBytes)}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Events in the trail</dt>
            <dd className="pt-tabular">{assessment.totalEventCount.toLocaleString('en-SG')}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Unarchived backlog</dt>
            <dd className="pt-tabular">{assessment.backlogCount.toLocaleString('en-SG')}</dd>
          </dl>
        )}

        {sweepOutcome !== null && (
          <div className="mt-4" role="status">
            {typeof sweepOutcome === 'string' ? (
              <ErrorCard title="The alert sweep did not run" message={sweepOutcome} />
            ) : (
              <div className="pt-well p-4">
                <p className="font-semibold">
                  Swept at this reading: level {sweepOutcome.status.level}.
                </p>
                {/* ⚠ VERBATIM, like the panel above — the server's own sentence, never paraphrased. */}
                <p className="text-sm mt-1">{plainProse(sweepOutcome.status.message)}</p>
                <NotificationOutcome outcome={sweepOutcome.notification} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── §12.134, rotation ───────────────────────────────────────────────── */}
      <div className="pt-card">
        <h2 className="text-lg font-semibold mb-1">Rotate the trail into a new segment</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] mb-4">
          Seals the unarchived backlog into one signed, encrypted segment. One invocation, one segment, over a bounded
          backlog. <strong>Nothing is deleted</strong>: <code>audit.event</code> refuses UPDATE, DELETE and TRUNCATE
          at the database, including for a superuser, so rotation adds a segment and removes no row.
        </p>
        <SgdsButton
          variant="primary"
          type="button"
          loading={running}
          disabled={running || confirming !== null}
          onClick={askToRotate}
        >
          Rotate now
        </SgdsButton>

        {confirming?.kind === 'rotate' && (
          <div className="mt-4">
            <ConfirmWrite
              summary={confirming.summary}
              confirmLabel="Yes, seal a segment"
              onConfirm={() => void rotate()}
              onCancel={() => setConfirming(null)}
            />
          </div>
        )}

        {runOutcome !== null && (
          <div className="mt-4" role="status">
            {typeof runOutcome === 'string' ? (
              <ErrorCard title="The rotation did not run" message={runOutcome} />
            ) : runOutcome.rotated ? (
              <div className="pt-well p-4">
                <p className="font-semibold">Sealed segment {runOutcome.segment.id}.</p>
                <p className="text-sm mt-1">
                  {runOutcome.segment.eventCount.toLocaleString('en-SG')} event(s), ids {runOutcome.segment.fromEventId}{' '}
                  to {runOutcome.segment.toEventId}. Signed with {runOutcome.segment.signingKeyId}, encrypted with{' '}
                  {runOutcome.segment.encryptionKeyId} ({runOutcome.segment.algorithm}).
                </p>
                <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">
                  Every one of those events is still readable in the trail. Verify the segment below to prove it rather
                  than taking this sentence for it.
                </p>
                <NotificationOutcome outcome={runOutcome.notification} />
              </div>
            ) : (
              /*
                ⚠ `rotated: false` IS A NORMAL ANSWER, not an error, and rendering it
                as one would teach an operator to distrust the control. An empty or
                too-small backlog has nothing to seal, and the server says which.
              */
              <div className="pt-well p-4">
                <p className="font-semibold">Nothing was sealed, and that is an answer rather than a failure.</p>
                <p className="text-sm mt-1">{plainProse(runOutcome.reason)}</p>
                <p className="text-sm mt-1 text-[var(--sgds-body-color-subtle)]">
                  Unarchived backlog at the time of the call: {runOutcome.backlog.toLocaleString('en-SG')} event(s).
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── The segment list, and per-segment verification ───────────────────── */}
      <div className="pt-card">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold mb-1">Segments</h2>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              Metadata only. Neither the ciphertext nor the initialisation vector is served to this screen, by design:
              what is here is what an operator needs to reconcile a segment, and nothing that would let a
              reader reconstruct the archived events outside the verification path.
            </p>
          </div>
          <IconButton glyph="refresh" label="Refresh" onClick={segments.reload} />
        </div>

        {segments.error !== null ? (
          <ErrorCard title="Could not read the segment list" message={segments.error} onRetry={segments.reload} />
        ) : segments.initialLoading ? (
          <LoadingCard label="Reading archive segments" rows={3} />
        ) : rows.length === 0 ? (
          /*
            ⚠ An EMPTY archive is a real state and says what it means. "No segments"
            with nothing else would read as a screen that failed to load — the
            four-dashboard-bands confusion this product records.
          */
          <EmptyCard
            title="No segments have been sealed"
            message="The trail has never been rotated on this deployment. Every event is still in audit.event and nothing is missing; there is simply no segment to list yet. Rotate above to seal the current backlog."
          />
        ) : (
          <>
          <SortAnnouncement
            sort={segmentSort.sort}
            interacted={segmentSort.interacted}
            busy={segments.loading}
            columnLabels={SEGMENT_COLUMN_LABELS}
            rowCount={rows.length}
          />
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">
                Sealed audit archive segments, with the event range, keys, digest and sealing time of each
              </caption>
              <thead>
                <tr className="text-left">
                  <SortableTh label={SEGMENT_COLUMN_LABELS.id} sortKey="id" sort={segmentSort.sort} onSort={onSegmentSort} />
                  <SortableTh label={SEGMENT_COLUMN_LABELS.eventCount} sortKey="eventCount" sort={segmentSort.sort} onSort={onSegmentSort} align="right" />
                  {/*
                    ⚠ The cell shows a RANGE (`from`–`to`) and the sort is on the LOWER
                    bound. Segments do not overlap — each seals the backlog the last one
                    left — so ordering by `from_event_id` is the same order as ordering
                    by `to_event_id`, and either answers "which segment covers the
                    earliest events". Sorting a range as a composite would need a
                    concatenation the whitelist deliberately cannot express.
                  */}
                  <SortableTh label={SEGMENT_COLUMN_LABELS.fromEventId} sortKey="fromEventId" sort={segmentSort.sort} onSort={onSegmentSort} />
                  <SortableTh label={SEGMENT_COLUMN_LABELS.fromOccurredAt} sortKey="fromOccurredAt" sort={segmentSort.sort} onSort={onSegmentSort} />
                  {/*
                    ⚠ Sorted by ALGORITHM, which is the first line of this cell — the
                    two key ids below it are the encryption and signing key, and an
                    incident review asking "which segments were sealed under the old
                    algorithm" is the question the column is for. Sorting by a key id
                    instead would answer a narrower question with the same header.
                  */}
                  <SortableTh label={SEGMENT_COLUMN_LABELS.algorithm} sortKey="algorithm" sort={segmentSort.sort} onSort={onSegmentSort} />
                  <SortableTh label={SEGMENT_COLUMN_LABELS.contentDigest} sortKey="contentDigest" sort={segmentSort.sort} onSort={onSegmentSort} />
                  <SortableTh label={SEGMENT_COLUMN_LABELS.createdAt} sortKey="createdAt" sort={segmentSort.sort} onSort={onSegmentSort} />
                  <th scope="col" className="py-2" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                    Verify
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((segment) => {
                  const outcome = verifications[segment.id]
                  return (
                    <tr key={segment.id} className="border-t border-[var(--sgds-border-color-default)] align-top">
                      <td className="py-2 pr-4">{digest(segment.id)}</td>
                      <td className="py-2 pr-4 pt-tabular">{segment.eventCount.toLocaleString('en-SG')}</td>
                      <td className="py-2 pr-4 pt-tabular">
                        {segment.fromEventId} to {segment.toEventId}
                      </td>
                      <td className="py-2 pr-4">
                        {formatInstant(segment.fromOccurredAt)}
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                          to {formatInstant(segment.toOccurredAt)}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        {segment.algorithm}
                        <span className="block text-[var(--sgds-body-color-subtle)]">
                          enc {segment.encryptionKeyId} / sig {segment.signingKeyId}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{digest(segment.contentDigest)}</td>
                      <td className="py-2 pr-4">
                        {formatInstant(segment.createdAt)}
                        <span className="block text-xs text-[var(--sgds-body-color-subtle)]">
                          {/*
                            ⚠ `null` means the segment was sealed by a process with no
                            account behind it, which is a fact worth showing rather
                            than an em dash: it is how an operator tells an
                            operator-invoked rotation from one a script ran.
                          */}
                          {segment.createdBy === null ? 'by a process with no account' : `by ${segment.createdBy}`}
                        </span>
                      </td>
                      <td className="py-2">
                        <SgdsButton
                          variant="outline"
                          type="button"
                          loading={verifying === segment.id}
                          onClick={() => void verify(segment.id)}
                        >
                          Verify
                        </SgdsButton>
                        {outcome !== undefined && (
                          <div className="mt-3">
                            {typeof outcome === 'string' ? (
                              <p className="text-sm text-[var(--sgds-danger-color-default)]">{outcome}</p>
                            ) : (
                              <VerificationVerdicts verification={outcome} />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {/*
            ★★★ THE SERVER'S OWN SENTENCE ABOUT THE PAGE — 2026-08-24 (WAVE 18), and
            UNCONDITIONAL now.

            ⚠ It used to render only when `paging.atCap` was true, and that was right for
            what it then said: *"the table you are reading is a window"* is FALSE on a
            trail rotated fewer than a hundred times, and an unconditional version would
            have taught an operator to distrust a complete list. Now that the list pages,
            the sentence states how many of how many are on screen — true in every state,
            so hiding it in any state would be the omission instead.

            ⚠ The words are still the SERVER's, so a change to the page size cannot leave
            this screen quoting the old one.
          */}
          {segments.data?.pagingNote !== undefined && (
            <p className="mt-4 text-sm text-[var(--sgds-color-subtle)]">{plainProse(segments.data.pagingNote)}</p>
          )}
          <Pagination pager={segmentPager} label="segments" total={segmentPaging?.total} shown={rows.length} />
          </>
        )}
      </div>
    </div>
  )
}
